import { useEffect, useState } from 'react'
import ErrorDisplay from '../../../components/error/ErrorDisplay'
import { RunInfo } from './NodeData'

import LiveData from './LiveData'
import { axiosPrivate } from '../../../utils/axiosInstance'
import Loading from '../../../Loading'

interface Props {
  runInfo: RunInfo
  isRunning: boolean
  sessionID: string
  fileName: string
  nodePath?: string | undefined
  deploySelected: any
}

interface State {
  connected: boolean
  runError: Error | null
}

/**
 * Manages live data streaming for a specific node in a configuration file.
 *
 * @param {object} props - The component's props.
 * @param {boolean} props.isRunning - Indicates whether the configuration is currently running.
 * @param {string} props.sessionID - The ID of the session.
 * @param {string} props.fileName - The name of the configuration file.
 * @param {string} props.nodePath - The path of the node for which live data is managed.
 * @returns {JSX.Element} The rendered component.
 */
export function NodeLiveDataHandler({ isRunning, sessionID, fileName, nodePath, deploySelected }: Props) {
  const [state, setState] = useState<State>({
    connected: false,
    runError: null,
  })

  const startStream = async (serverId: string) => {
    try {
      // POST live/session/:id/file/:name
      const url = `/mqttapi/v1/live/session/${sessionID}/file/${fileName}/server/${serverId}`
      const resp = await axiosPrivate.post(url, nodePath ? { path: nodePath } : {})
      if (resp && resp.data && resp.data.status === 1) {
        setState({
          ...state,
          connected: true,
          runError: null,
        })
        return
      } else {
        setState({
          ...state,
          runError: resp.data,
        })
      }
      return
    } catch (err: any) {
      setState({
        ...state,
        runError: err,
      })
    }
  }

  const closeStream = async (serverId: string) => {
    try {
      // DELETE live/session/:id/file/:name
      const url = `/mqttapi/v1/live/session/${sessionID}/file/${fileName}/server/${serverId}`
      const resp = await axiosPrivate.delete(url)
      if (resp && resp.data && resp.data.status === 0) {
        setState({
          ...state,
          connected: true,
          runError: null,
        })
        return
      } else {
        setState({
          ...state,
          runError: resp.data?.message || 'error in stopping',
        })
        return
      }
    } catch (err: any) {
      setState({
        ...state,
        runError: err,
      })
      return
    } finally {
      setState({
        ...state,
        connected: true,
      })
    }
  }

  useEffect(() => {
    if (deploySelected !== '') {
      if (isRunning) {
        // Start it
        ;(async () => {
          await startStream(deploySelected)
        })()
      }
      if (!isRunning && state.connected) {
        // Stop it
        ;(async () => {
          await closeStream(deploySelected)
        })()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning])

  if (!isRunning) {
    return <div>Not Running</div>
  }

  const { connected, runError } = state

  return (
    <div>
      {/* RUN NOT FINISHED */}
      {!connected && deploySelected !== '' && (
        <div>
          <Loading action={'Loading...'} />
        </div>
      )}

      {/* RUN REQUEST FAILED */}
      {runError !== null ? <ErrorDisplay err={runError} /> : null}

      {deploySelected !== '' && connected && (
        <LiveData
          sessionID={sessionID}
          fileName={fileName}
          connected={connected}
          closeStream={closeStream}
          startStream={startStream}
          deploySelected={deploySelected}
        />
      )}
      {deploySelected === '' && <h6 style={{ color: 'red' }}>First, please select the studio server!</h6>}
    </div>
  )
}

export default NodeLiveDataHandler

// ! https://github.com/mqttjs/MQTT.js/issues/1412
// ! https://github.com/mqttjs/MQTT.js/issues/1020
